import React, { useState } from 'react';
import { assignBlockReason, toMinutes, PX_PER_HOUR } from './scheduleUtils';
import { localTime } from '../../../utils/datetime';
import { useTheme } from '../../../context/ThemeContext';
import { UserPhoto } from '../../../components';
import { unavailabilityLabel } from '../../../utils/leaveTypes';

// Soft diagonal-hatch GREEN fill for the carer availability lane. Light mode
// uses a pale green wash; dark mode uses a translucent green so the band reads
// as "available" without glaring against the dark board.
const HATCH_LIGHT = {
  backgroundColor: '#effbf5',
  backgroundImage: 'repeating-linear-gradient(135deg, rgba(16,185,129,0.12) 0, rgba(16,185,129,0.12) 6px, rgba(16,185,129,0.03) 6px, rgba(16,185,129,0.03) 12px)',
};
const HATCH_DARK = {
  backgroundColor: 'rgba(16,185,129,0.07)',
  backgroundImage: 'repeating-linear-gradient(135deg, rgba(16,185,129,0.20) 0, rgba(16,185,129,0.20) 6px, rgba(16,185,129,0.06) 6px, rgba(16,185,129,0.06) 12px)',
};

// One carer = a left identity cell + a right timeline track holding:
//   • the green availability band (their shift for the day) with thin white
//     hour gridlines showing through and an "Available HH:MM–HH:MM" label
//   • darker green one-off extra-availability bands
//   • a clickable grey "time away" band when on holiday/sick (opens the leave
//     editor so the manager can update or delete it)
//   • visit cards laid out as an even FLOW inside the lane (Scheduled over
//     Actual). They are NOT positioned by time — they share the lane width
//     evenly, growing when there are few and shrinking when there are many, so
//     they never overlap.
const CarerRow = ({ carer, geo, hourWidths = [], onVisitClick, onLeaveClick, onCarerDetails, onReviewVisit, onShiftClick, dragActive, dragVisit, onDropVisit }) => {
  const [dropHover, setDropHover] = useState(false);
  const { theme } = useTheme();
  const HATCH = theme === 'dark' ? HATCH_DARK : HATCH_LIGHT;

  const visits = (carer.visits || []).map((v) => {
    const aStart = v.actual_start ? localTime(v.actual_start) : null;
    const aEnd = v.actual_end ? localTime(v.actual_end) : null;
    const hasActual = !!aStart;
    // A reviewed/approved visit no longer flags as differing.
    const differs = !v.__reviewed && !v.reviewed
      && hasActual && (aStart !== v.start || (aEnd && aEnd !== v.end));
    return { ...v, aStart, aEnd, hasActual, differs };
  });
  const rowHasVisit = visits.length > 0;
  const onLeave = !!carer.time_away;
  const hasExtra = !!(carer.extra_availability && carer.extra_availability.length);
  // "Not scheduled" = no shift, no visits, no extra availability. Treated like a
  // day off: a compact, disabled band (just like holiday) — clicking it opens
  // the Add-availability modal.
  const notScheduled = !onLeave && !carer.shift && !rowHasVisit && !hasExtra;
  const offDay = onLeave || notScheduled;
  const shiftGeo = carer.shift ? geo(carer.shift.start, carer.shift.end) : null;

  const pad = 10;
  const MIN_CARD_PX = 120; // wide enough that the client name stays readable, even on a short visit
  // How many minutes MIN_CARD_PX is worth at the current density. Used only for
  // lane packing so two cards that would visually collide drop onto separate
  // lanes — the card itself always spans its EXACT scheduled time.
  const minSlotMin = Math.round((MIN_CARD_PX / PX_PER_HOUR) * 60);

  // Each visit is positioned at its SCHEDULED time on the timeline (geo) and
  // spans exactly start→end. The Actual box renders directly under the Scheduled
  // box at the SAME slot — a late clock-in only changes the Actual text, never
  // the position. Visits that would overlap are packed onto separate lanes.
  const positioned = visits.map((v) => {
    const s = toMinutes(v.start);
    let e = toMinutes(v.end);
    if (s != null && (e == null || e <= s)) e = s + 45;
    const g = geo(v.start, v.end) || { left: '0%', width: '5%' };
    // packEnd = whichever is later, the real end or the min-width footprint, so
    // a 07:30–08:15 and 08:30–09:16 (15-min gap) stay side by side, while two
    // near-touching short visits get stacked instead of overlapping.
    return { ...v, _s: s, _e: e, _left: g.left, _width: g.width, _packEnd: Math.max(e, s + minSlotMin) };
  }).filter((v) => v._s != null);
  // Greedy lane packing by start time: reuse a lane once its last card's slot
  // (real end OR min-width footprint) has passed.
  const laneEnds = [];
  [...positioned]
    .sort((a, b) => a._s - b._s)
    .forEach((v) => {
      let lane = laneEnds.findIndex((end) => v._s >= end);
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(v._packEnd); }
      else laneEnds[lane] = v._packEnd;
      v._lane = lane;
    });
  const laneCount = laneEnds.length;

  const LANE_H = 138;   // px per lane (Scheduled box + Actual box stacked)
  const LANE_GAP = 8;
  const CARD_VPAD = 12; // breathing room so cards don't touch the green band edges
  const visitsH = laneCount > 0 ? laneCount * LANE_H + (laneCount - 1) * LANE_GAP : 0;
  // Off rows stay compact; otherwise the track grows with the number of lanes.
  // The extra CARD_VPAD top+bottom keeps the cards clear of the band edges.
  const rowH = offDay ? 84 : (rowHasVisit ? pad * 2 + CARD_VPAD * 2 + Math.max(visitsH, 120) : 120);

  // Drag-and-drop target: an unassigned visit can be dropped onto this carer.
  // While hovering, the row gets a GREEN ring (droppable) or a RED ring when the
  // carer can't take it — on leave, already on the visit, or double-booked at
  // the same time. The drop itself is blocked + warned about by the parent.
  const dropBlocked = dragActive ? !!assignBlockReason(carer, dragVisit) : false;
  const dropProps = dragActive ? {
    onDragEnter: (e) => { e.preventDefault(); setDropHover(true); },
    // Always allow the drop to FIRE (dropEffect 'move'), even for a blocked
    // carer — the parent decides whether to assign and shows a warning toast if
    // not. 'none' would silently swallow the drop and the user would see nothing.
    onDragOver: (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; },
    onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDropHover(false); },
    onDrop: (e) => { e.preventDefault(); setDropHover(false); onDropVisit && onDropVisit(carer); },
  } : {};
  const dropRing = dropHover
    ? (dropBlocked ? 'ring-2 ring-inset ring-rose-500 bg-rose-50/40' : 'ring-2 ring-inset ring-emerald-500 bg-emerald-50/40')
    : '';

  return (
    <div className={`relative flex border-t border-gray-100 dark:border-gray-800 transition ${dropRing}`} {...dropProps}>
      {/* Identity — sticky so it stays frozen while the timeline scrolls. The
          carer shows as a centred avatar with the name beneath it. */}
      <div
        className={`sticky left-0 z-[28] flex w-[180px] flex-shrink-0 flex-col items-center justify-center gap-1 border-r px-3 text-center transition ${
          dropHover ? (dropBlocked ? 'border-rose-300 bg-rose-50' : 'border-emerald-300 bg-emerald-50 dark:border-emerald-900/50 dark:bg-emerald-900/10') : 'border-gray-100 bg-white dark:border-gray-800 dark:bg-[#262b33]'
        }`}
        style={{ minHeight: rowH }}
      >
        <UserPhoto
          src={carer.avatar_url}
          alt={carer.name}
          className={`rounded-full shadow-sm ${offDay ? 'h-8 w-8' : 'h-11 w-11'}`}
          fallback={(
            <span className={`grid flex-shrink-0 place-items-center rounded-full bg-indigo-500 font-bold text-white shadow-sm ${offDay ? 'h-8 w-8 text-[11px]' : 'h-11 w-11 text-[13px]'}`}>{carer.initials}</span>
          )}
        />
        <div className="w-full min-w-0">
          <div className="truncate text-[13px] font-bold text-gray-800 dark:text-gray-200">{carer.name}</div>
          {/* Off carers (leave / not scheduled) show NOTHING below the name —
              the disabled band in the timeline conveys the status. */}
          {!offDay && (
            <div className="truncate text-[10px] text-gray-400 dark:text-gray-500">
              {[
                carer.shift ? `${carer.shift.start}–${carer.shift.end}` : hasExtra ? `Extra ${carer.extra_availability[0].start}–${carer.extra_availability[0].end}` : null,
                visits.length > 0 ? `${visits.length} visit${visits.length > 1 ? 's' : ''}` : null,
              ].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>

        {/* Scheduled carers get a "View details" link; off carers rely on their
            disabled timeline band (no day-off text below the name). */}
        {!offDay && onCarerDetails && (
          <button onClick={() => onCarerDetails(carer)} className="text-[10px] font-semibold text-blue-600 hover:underline">
            View details
          </button>
        )}
      </div>

      {/* Timeline track */}
      <div className="relative flex-1" style={{ height: rowH }}>
        {/* standing shift availability band (hatched green, with the
            "Available" label) — sits at the bottom of the stack */}
        {shiftGeo && (
          <button
            type="button"
            onClick={() => onShiftClick && onShiftClick(carer)}
            title="Add availability for this carer"
            className="absolute z-0 rounded-md p-0 text-left ring-1 ring-inset ring-emerald-200 dark:ring-emerald-500/30 transition hover:ring-2 hover:ring-emerald-400 dark:hover:ring-emerald-500/60"
            style={{ ...HATCH, ...shiftGeo, top: pad, bottom: pad }}
          >
            <span className="absolute bottom-1 right-2 text-[10px] font-bold text-emerald-600/90 dark:text-emerald-400/90">
              Available {carer.shift.start}–{carer.shift.end}
            </span>
          </button>
        )}

        {/* one-off extra availability bands — clickable so the manager can add
            more availability for this carer even once they already have some
            (a carer with no standing shift has no other entry point otherwise). */}
        {(carer.extra_availability || []).map((ea, i) => {
          const eaGeo = geo(ea.start, ea.end);
          if (!eaGeo) return null;
          return (
            <button
              key={`ea-${i}`}
              type="button"
              onClick={() => onShiftClick && onShiftClick(carer, ea)}
              title={`Extra availability ${ea.start}–${ea.end}${ea.note ? ` · ${ea.note}` : ''} · tap to add availability`}
              className={`absolute z-0 rounded-md bg-emerald-200/70 dark:bg-emerald-500/20 p-0 text-left ring-1 ring-inset ring-emerald-400 dark:ring-emerald-500/40 transition hover:ring-2 hover:ring-emerald-500 ${ea.__draft ? 'ring-2 ring-amber-400' : ''}`}
              style={{ ...eaGeo, top: pad, bottom: pad }}
            >
              <span className="absolute left-1 top-0.5 text-[8px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">+{ea.start}–{ea.end}</span>
            </button>
          );
        })}

        {/* Hour gridlines — drawn ABOVE the green band and across the WHOLE row
            so the hourly marks read equally inside the availability band AND in
            the empty area outside it. Grey so they stay visible on both the
            green band and the plain white background. */}
        <div className="pointer-events-none absolute inset-0 z-[1] flex">
          {hourWidths.map((w, i) => <div key={i} style={{ flexGrow: w, flexBasis: 0 }} className="border-r border-gray-200 dark:border-gray-800" />)}
        </div>

        {/* time away — a clickable "deactivated" band that opens the leave
            editor (update / delete). */}
        {carer.time_away && (
          <button
            type="button"
            onClick={() => onLeaveClick && onLeaveClick(carer)}
            title="Edit or delete this time away"
            className={`absolute inset-x-0 z-[2] flex items-center justify-center gap-2 rounded-md border border-dashed border-gray-300 dark:border-gray-600 bg-gray-100/90 dark:bg-[#2a2f38]/90 text-[12px] font-semibold text-gray-500 dark:text-gray-400 transition hover:bg-gray-200/90 dark:hover:bg-[#333944]/90 ${carer.time_away.__draft ? 'ring-2 ring-amber-400' : ''}`}
            style={{ top: pad, bottom: pad }}
          >
            <span className="grid h-5 w-5 place-items-center rounded-full bg-gray-300 dark:bg-gray-600 text-[10px] text-white">⏻</span>
            <span className="uppercase tracking-wide">{unavailabilityLabel(carer.time_away)}</span>
            <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500">· Whole day · tap to edit</span>
          </button>
        )}

        {/* Not scheduled today — a disabled "day off" band (just like holiday).
            Clicking it opens the Add-availability modal to give this carer a
            shift for the day. */}
        {notScheduled && (
          <button
            type="button"
            onClick={() => onShiftClick && onShiftClick(carer)}
            title="Add availability for this carer"
            className="absolute inset-x-0 z-[2] flex items-center justify-center gap-2 rounded-md border border-dashed border-gray-300 dark:border-gray-600 bg-gray-100/90 dark:bg-[#2a2f38]/90 text-[12px] font-semibold text-gray-500 dark:text-gray-400 transition hover:bg-gray-200/90 dark:hover:bg-[#333944]/90"
            style={{ top: pad, bottom: pad }}
          >
            <span className="grid h-5 w-5 place-items-center rounded-full bg-gray-300 dark:bg-gray-600 text-[10px] text-white">⏻</span>
            <span className="uppercase tracking-wide">Day off</span>
            <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500">· tap to add availability</span>
          </button>
        )}

        {/* Visit cards positioned by their SCHEDULED time on the timeline. The
            card spans from its start to its end (so a 07:00–10:00 visit sits
            between 07 and 10). The Actual box sits directly UNDER the Scheduled
            box at the SAME slot — a late clock-in only changes the Actual text,
            it never moves the box. Overlapping visits drop onto separate lanes. */}
        {!carer.time_away && rowHasVisit && (
          <div className="pointer-events-none absolute inset-0 z-[2]">
            {positioned.map((v) => {
              const draftRing = v.__draft ? 'ring-2 ring-amber-400' : '';
              const schedTime = `${v.start}${v.end ? `–${v.end}` : ''}`;
              const actTime = `${v.aStart}${v.aEnd ? `–${v.aEnd}` : ''}`;
              const isMissed = !v.__reviewed && !v.reviewed && v.status === 'missed';
              const needsReview = isMissed || (!v.__reviewed && !v.reviewed && v.differs);
              const top = pad + CARD_VPAD + v._lane * (LANE_H + LANE_GAP);
              const reviewBtn = needsReview && onReviewVisit && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); onReviewVisit(carer, v); }}
                  title="Review this visit"
                  className="flex-shrink-0 cursor-pointer rounded bg-rose-100 dark:bg-rose-900/30 px-1.5 py-0.5 text-[8px] font-bold uppercase text-rose-600 dark:text-rose-400 hover:bg-rose-200 dark:hover:bg-rose-900/50"
                >
                  Review
                </span>
              );
              const differsBadge = v.differs && (
                <span className="flex-shrink-0 rounded bg-amber-100 px-1 py-0.5 text-[7px] font-bold uppercase text-amber-600 dark:bg-amber-900/10 dark:text-amber-400">Differs</span>
              );
              return (
                <div
                  key={`${v.assignment_id}-${v.start}`}
                  className="pointer-events-auto absolute flex flex-col gap-1"
                  style={{ left: v._left, width: v._width, minWidth: MIN_CARD_PX, top, height: LANE_H, paddingRight: 4 }}
                >
                  {/* Scheduled — stacked: Name · package · Sched label ·
                      Differs (or Review when missed) · time. */}
                  <button
                    onClick={() => onVisitClick({ ...v, _carerId: carer.id })}
                    title={`${v.resident.name} · ${v.package_name} · ${schedTime}${isMissed ? ' · Missed' : ''}`}
                    style={{ borderLeftColor: isMissed ? '#ef4444' : '#2563eb' }}
                    className={`flex h-[74px] flex-shrink-0 flex-col justify-between overflow-hidden rounded-md border border-l-4 px-2 py-1 text-left hover:brightness-95 ${isMissed ? 'border-rose-300 bg-rose-50 dark:border-rose-900/50 dark:bg-rose-900/20' : 'border-blue-300 bg-blue-50 dark:border-blue-900/50 dark:bg-blue-900/20'} ${draftRing}`}
                  >
                    <span className={`truncate text-[11px] font-bold leading-tight ${isMissed ? 'text-rose-700 dark:text-rose-400' : 'text-blue-900 dark:text-blue-400'}`}>{v.resident.name}</span>
                    {v.package_name && (
                      <div className={`truncate text-[8px] font-bold uppercase leading-tight ${isMissed ? 'text-rose-500/80 dark:text-rose-400/80' : 'text-blue-600/70 dark:text-blue-400'}`}>{v.package_name}</div>
                    )}
                    {/* "Scheduled" label + DIFFERS badge share one row (Review
                        here instead for a missed visit, which has no Actual card). */}
                    <div className="flex min-w-0 items-center justify-between gap-1 leading-none">
                      <span className={`flex-shrink-0 text-[8px] font-bold uppercase ${isMissed ? 'text-rose-600/80 dark:text-rose-400/80' : 'text-blue-700/70 dark:text-blue-400'}`}>{isMissed ? 'Missed' : 'Scheduled'}</span>
                      {isMissed ? reviewBtn : differsBadge}
                    </div>
                    <span className={`truncate text-[10px] font-bold leading-tight ${isMissed ? 'text-rose-700 dark:text-rose-400' : 'text-blue-700 dark:text-blue-400'}`}>{schedTime}</span>
                  </button>

                  {/* Actual (amber) or awaiting placeholder — same slot as Sched.
                      Stacked: Actual label · time · Review (when it differs). */}
                  {v.hasActual ? (
                    <button
                      onClick={() => onVisitClick({ ...v, _carerId: carer.id })}
                      title={`Actual ${actTime}`}
                      style={{ borderLeftColor: '#f59e0b' }}
                      className="flex min-h-0 flex-1 flex-col justify-between overflow-hidden rounded-md border border-l-4 border-amber-300 bg-amber-50 px-2 py-1 text-left hover:brightness-95 dark:border-amber-900/50 dark:bg-amber-900/10"
                    >
                      {/* "Actual" label + REVIEW button share the top row so the
                          time below gets the full width and never truncates. */}
                      <div className="flex min-w-0 items-center justify-between gap-1 leading-none">
                        <span className="truncate text-[10px] font-bold text-amber-700 dark:text-amber-400">Actual</span>
                        {v.differs && reviewBtn}
                      </div>
                      <span className="truncate text-[10px] font-bold leading-tight text-amber-700 dark:text-amber-400">{actTime}</span>
                    </button>
                  ) : (
                    <div
                      className="flex min-h-0 flex-1 flex-col justify-between overflow-hidden rounded-md border border-dashed border-gray-300 bg-gray-50/60 dark:bg-[#20242c] px-2 py-1"
                      title="No clock-in yet"
                    >
                      <span className="truncate text-[10px] font-bold leading-tight text-gray-400 dark:text-gray-500">Actual</span>
                      <span className="truncate text-[9px] leading-none text-gray-400 dark:text-gray-500">Awaiting</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CarerRow;
